import { useEffect, useRef, useState } from 'react';
import type {
    MegaGridColumn,
    MegaGridDensity,
    MegaGridFilterInstance,
    MegaGridFilterOp,
    MegaGridGroupBy,
    MegaGridSort,
    MegaGridView,
    MegaGridViewState,
} from './MegaGridTypes';
import { container } from 'tsyringe';
import { GridViewsService } from '@/shared/grid-views/GridViewsService';
import { makeViewId, type MegaGridPersisted } from './MegaGridStorage';

/**
 * Resolves the server-side views store, or null when DI isn't wired (tests,
 * storybook) — in which case the grid falls back to localStorage only.
 */
function resolveViewsStore(): GridViewsService | null {
    try {
        return container.resolve(GridViewsService);
    } catch {
        return null;
    }
}

interface UseMegaGridStateArgs<T> {
    columns: MegaGridColumn<T>[];
    persistenceKey?: string;
    defaultDensity: MegaGridDensity;
    defaultGroupBy: string[];
    allowMultiSort: boolean;
    /** Seeds the grid's default sort (persisted/user state still overrides it). */
    defaultSort?: MegaGridSort;
    /** Filters baked into the grid's default state (e.g. a drill-in's navigation context) — removable/editable like any user filter, and restored by "Reset to default". */
    defaultFilters?: MegaGridFilterInstance[];
    /** Predefined views (e.g. pinned segment presets) seeded once by id — user edits/deletes persist and are never re-seeded over. */
    seedViews?: MegaGridView[];
}

/** Adds any seed view whose id isn't already persisted, so presets appear for fresh users without clobbering saved edits. */
function mergeSeedViews(persisted: MegaGridView[], seed: MegaGridView[] = []): MegaGridView[] {
    const existing = new Set(persisted.map((view) => view.id));
    return [...persisted, ...seed.filter((view) => !existing.has(view.id))];
}

function buildDefaultState<T>(columns: MegaGridColumn<T>[], density: MegaGridDensity, groupBy: string[], defaultFilters: MegaGridFilterInstance[] = [], defaultSort?: MegaGridSort): MegaGridViewState {
    return {
        search: '',
        sorts: defaultSort ? [defaultSort] : [],
        filters: defaultFilters,
        facets: {},
        columnOrder: columns.map((c) => c.key),
        hidden: columns.filter((c) => c.defaultHidden).map((c) => c.key),
        widths: {},
        pinned: [],
        groupBy: groupBy.map((columnKey): MegaGridGroupBy => ({ columnKey, sortMode: 'count', sortDir: 'desc' })),
        density,
    };
}

/**
 * Drops references to columns that no longer exist and appends new ones. Also
 * tolerant of state persisted under an older schema (e.g. `filters` once was an
 * object): any field that isn't the expected array is coerced to a safe default.
 */
function reconcile<T>(state: MegaGridViewState, columns: MegaGridColumn<T>[]): MegaGridViewState {
    const keys = new Set(columns.map((c) => c.key));
    const order = (Array.isArray(state.columnOrder) ? state.columnOrder : []).filter((k) => keys.has(k));
    for (const c of columns) if (!order.includes(c.key)) order.push(c.key);
    return {
        ...state,
        sorts: Array.isArray(state.sorts) ? state.sorts : [],
        facets: state.facets && typeof state.facets === 'object' ? state.facets : {},
        widths: state.widths && typeof state.widths === 'object' ? state.widths : {},
        columnOrder: order,
        hidden: (Array.isArray(state.hidden) ? state.hidden : []).filter((k) => keys.has(k)),
        pinned: (Array.isArray(state.pinned) ? state.pinned : []).filter((k) => keys.has(k)),
        groupBy: ((Array.isArray(state.groupBy) ? state.groupBy : []) as unknown[])
            .map((g): MegaGridGroupBy => (typeof g === 'string' ? { columnKey: g, sortMode: 'count', sortDir: 'desc' } : (g as MegaGridGroupBy)))
            .filter((g) => keys.has(g.columnKey)),
        filters: (Array.isArray(state.filters) ? state.filters : []).filter((f) => keys.has(f.columnKey)),
    };
}

export interface MegaGridController {
    state: MegaGridViewState;
    views: MegaGridView[];
    activeViewId: string | null;
    expanded: Set<string>;
    setSearch: (value: string) => void;
    toggleSort: (key: string, additive: boolean) => void;
    addFilter: (columnKey: string, op: MegaGridFilterOp) => void;
    updateFilter: (id: string, patch: Partial<MegaGridFilterInstance>) => void;
    removeFilter: (id: string) => void;
    clearFilters: () => void;
    setFacet: (key: string, value: string) => void;
    setColumnVisible: (key: string, visible: boolean) => void;
    reorderColumns: (order: string[]) => void;
    setColumnWidth: (key: string, width: number) => void;
    togglePin: (key: string) => void;
    addGroupBy: (columnKey: string) => void;
    removeGroupBy: (columnKey: string) => void;
    reorderGroupBy: (columnKeys: string[]) => void;
    setGroupSort: (columnKey: string, sortMode: 'count' | 'value', sortDir: 'asc' | 'desc') => void;
    clearGroupBy: () => void;
    setDensity: (density: MegaGridDensity) => void;
    toggleGroup: (id: string) => void;
    applyView: (view: MegaGridView) => void;
    saveView: (name: string, snapshot?: MegaGridViewState) => void;
    renameView: (id: string, name: string) => void;
    deleteView: (id: string) => void;
    togglePinView: (id: string) => void;
    resetView: () => void;
    /**
     * Merges a partial view-state patch in one atomic update (reconciled against
     * the current columns) and returns the resulting state — the entry point for
     * batch mutations like assist plans, where chaining individual setters would
     * clobber each other within a tick.
     */
    applyStatePatch: (patch: Partial<MegaGridViewState>, options?: MegaGridPatchOptions) => MegaGridViewState;
}

export interface MegaGridPatchOptions {
    /** What the patch merges over: the current state (default), the grid's default view, or a saved view's state. */
    base?: 'current' | 'default' | MegaGridView;
    /** Persists the merged result as a new named view and makes it active. */
    saveAs?: string;
}

export function useMegaGridState<T>(args: UseMegaGridStateArgs<T>): MegaGridController {
    const { columns, persistenceKey, defaultDensity, defaultGroupBy, allowMultiSort, defaultFilters, defaultSort, seedViews } = args;

    const filterSeq = useRef(0);

    /**
     * Server-side persistence is the sole source of truth (no localStorage): the
     * grid mounts with default state, hydrates from the store on mount, and
     * writes through (debounced) on change.
     */
    const store = useRef<GridViewsService | null | undefined>(undefined);
    if (store.current === undefined) store.current = resolveViewsStore();
    const columnsRef = useRef(columns);
    columnsRef.current = columns;
    const seedViewsRef = useRef(seedViews);
    seedViewsRef.current = seedViews;
    /** Gates write-through until the initial server load resolves, so the mount's default state can't clobber the stored copy before we've read it. */
    const hydrationDone = useRef(false);

    const [views, setViews] = useState<MegaGridView[]>(() => mergeSeedViews([], seedViews));
    const [activeViewId, setActiveViewId] = useState<string | null>(null);
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const [state, setState] = useState<MegaGridViewState>(() => buildDefaultState(columns, defaultDensity, defaultGroupBy, defaultFilters, defaultSort));

    useEffect(() => {
        const remote = store.current;
        if (!remote || !persistenceKey) {
            hydrationDone.current = true;
            return;
        }
        let cancelled = false;
        void remote
            .load(persistenceKey)
            .then((data) => {
                if (cancelled || !data) return;
                const remoteViews = Array.isArray(data['views']) ? (data['views'] as MegaGridView[]) : [];
                if (remoteViews.length > 0) setViews(mergeSeedViews(remoteViews, seedViewsRef.current));
                const remoteCurrent = (data['current'] ?? null) as MegaGridViewState | null;
                if (remoteCurrent) setState((prev) => reconcile({ ...prev, ...remoteCurrent }, columnsRef.current));
                const remoteActive = (data['activeViewId'] ?? null) as string | null;
                if (remoteActive !== null) setActiveViewId(remoteActive);
            })
            .finally(() => {
                if (!cancelled) hydrationDone.current = true;
            });
        return () => {
            cancelled = true;
        };
    }, [persistenceKey]);

    useEffect(() => {
        if (!persistenceKey || !hydrationDone.current) return;
        const data: MegaGridPersisted = { current: state, views, activeViewId };
        store.current?.save(persistenceKey, data as unknown as Record<string, unknown>);
    }, [persistenceKey, state, views, activeViewId]);

    const patch = (next: Partial<MegaGridViewState>, keepActive = false): void => {
        setState((prev) => ({ ...prev, ...next }));
        if (!keepActive) setActiveViewId(null);
    };

    const setSearch = (value: string): void => patch({ search: value }, true);

    const toggleSort = (key: string, additive: boolean): void => {
        setState((prev) => {
            const existing = prev.sorts.find((s) => s.key === key);
            if (!allowMultiSort || !additive) {
                if (!existing) return { ...prev, sorts: [{ key, dir: 'asc' }] };
                if (existing.dir === 'asc') return { ...prev, sorts: [{ key, dir: 'desc' }] };
                return { ...prev, sorts: [] };
            }
            if (!existing) return { ...prev, sorts: [...prev.sorts, { key, dir: 'asc' }] };
            if (existing.dir === 'asc') return { ...prev, sorts: prev.sorts.map((s) => (s.key === key ? { key, dir: 'desc' } : s)) };
            return { ...prev, sorts: prev.sorts.filter((s) => s.key !== key) };
        });
        setActiveViewId(null);
    };

    const addFilter = (columnKey: string, op: MegaGridFilterOp): void => {
        filterSeq.current += 1;
        const instance: MegaGridFilterInstance = { id: `f${filterSeq.current}`, columnKey, op };
        patch({ filters: [...state.filters, instance] });
    };
    const updateFilter = (id: string, change: Partial<MegaGridFilterInstance>): void =>
        patch({ filters: state.filters.map((f) => (f.id === id ? { ...f, ...change } : f)) });
    const removeFilter = (id: string): void => patch({ filters: state.filters.filter((f) => f.id !== id) });
    const clearFilters = (): void => patch({ filters: [] });

    const setFacet = (key: string, value: string): void => patch({ facets: { ...state.facets, [key]: value } });

    const setColumnVisible = (key: string, visible: boolean): void => {
        const hidden = visible ? state.hidden.filter((k) => k !== key) : [...state.hidden, key];
        patch({ hidden });
    };

    const reorderColumns = (order: string[]): void => patch({ columnOrder: order });
    const setColumnWidth = (key: string, width: number): void => patch({ widths: { ...state.widths, [key]: Math.max(48, Math.round(width)) } });
    const togglePin = (key: string): void => {
        const pinned = state.pinned.includes(key) ? state.pinned.filter((k) => k !== key) : [...state.pinned, key];
        patch({ pinned });
    };
    const addGroupBy = (columnKey: string): void => {
        if (state.groupBy.some((g) => g.columnKey === columnKey)) return;
        patch({ groupBy: [...state.groupBy, { columnKey, sortMode: 'count', sortDir: 'desc' }] });
        setExpanded(new Set());
    };
    const removeGroupBy = (columnKey: string): void => {
        patch({ groupBy: state.groupBy.filter((g) => g.columnKey !== columnKey) });
        setExpanded(new Set());
    };
    const reorderGroupBy = (columnKeys: string[]): void => {
        const byKey = new Map(state.groupBy.map((g) => [g.columnKey, g]));
        patch({ groupBy: columnKeys.map((k) => byKey.get(k)).filter((g): g is MegaGridGroupBy => Boolean(g)) });
        setExpanded(new Set());
    };
    const setGroupSort = (columnKey: string, sortMode: 'count' | 'value', sortDir: 'asc' | 'desc'): void =>
        patch({ groupBy: state.groupBy.map((g) => (g.columnKey === columnKey ? { ...g, sortMode, sortDir } : g)) });
    const clearGroupBy = (): void => {
        patch({ groupBy: [] });
        setExpanded(new Set());
    };
    const setDensity = (density: MegaGridDensity): void => patch({ density });

    const toggleGroup = (id: string): void => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const applyView = (view: MegaGridView): void => {
        setState(reconcile(view.state, columns));
        setActiveViewId(view.id);
        setExpanded(new Set());
    };

    const saveView = (name: string, snapshot?: MegaGridViewState): void => {
        setViews((prev) => {
            const id = makeViewId(prev);
            const view: MegaGridView = { id, name, state: snapshot ?? state };
            setActiveViewId(id);
            return [...prev, view];
        });
    };

    const renameView = (id: string, name: string): void => setViews((prev) => prev.map((v) => (v.id === id ? { ...v, name } : v)));
    const togglePinView = (id: string): void => setViews((prev) => prev.map((v) => (v.id === id ? { ...v, pinned: !(v.pinned ?? false) } : v)));
    const deleteView = (id: string): void => {
        setViews((prev) => prev.filter((v) => v.id !== id));
        setActiveViewId((prev) => (prev === id ? null : prev));
    };

    const resetView = (): void => {
        setState(buildDefaultState(columns, defaultDensity, defaultGroupBy, defaultFilters, defaultSort));
        setActiveViewId(null);
        setExpanded(new Set());
    };

    const applyStatePatch = (next: Partial<MegaGridViewState>, options?: MegaGridPatchOptions): MegaGridViewState => {
        const baseOption = options?.base ?? 'current';
        const baseState = baseOption === 'default' ? buildDefaultState(columns, defaultDensity, defaultGroupBy, defaultFilters, defaultSort) : baseOption === 'current' ? state : baseOption.state;
        const merged = reconcile({ ...baseState, ...next }, columns);
        setState(merged);
        if (next.groupBy !== undefined || baseOption !== 'current') setExpanded(new Set());
        if (options?.saveAs !== undefined) {
            saveView(options.saveAs, merged);
        } else {
            setActiveViewId(baseOption !== 'current' && baseOption !== 'default' && Object.keys(next).length === 0 ? baseOption.id : null);
        }
        return merged;
    };

    return {
        state,
        views,
        activeViewId,
        expanded,
        setSearch,
        toggleSort,
        addFilter,
        updateFilter,
        removeFilter,
        clearFilters,
        setFacet,
        setColumnVisible,
        reorderColumns,
        setColumnWidth,
        togglePin,
        addGroupBy,
        removeGroupBy,
        reorderGroupBy,
        setGroupSort,
        clearGroupBy,
        setDensity,
        toggleGroup,
        applyView,
        saveView,
        renameView,
        deleteView,
        togglePinView,
        resetView,
        applyStatePatch,
    };
}
